/**
 * Le journal d'audit dans les mots des utilisateurs.
 *
 * L'API stocke un identifiant technique (`auth.verrouillage`) : c'est ce qui
 * permet de filtrer et d'agréger. L'écran, lui, s'adresse à un administrateur
 * — pas à un développeur. La table vit ici, et non dans l'écran, parce que
 * l'API en a besoin aussi : chercher « fusion » doit trouver les fusions, et
 * trier par événement doit suivre l'ordre des libellés que l'on lit.
 *
 * La table est ouverte : une action absente d'ici s'affiche par son code
 * technique. C'est volontaire — un événement qu'on ne sait pas nommer doit se
 * VOIR, pas disparaître derrière un libellé générique.
 */
export const LIBELLE_ACTION_AUDIT = {
    // Connexions et sécurité
    'auth.connexion': 'Connexion réussie',
    'auth.echec': 'Échec de connexion',
    'auth.verrouillage': 'Compte verrouillé — échecs consécutifs',
    'auth.refus_compte_verrouille': 'Connexion refusée — compte verrouillé',
    'auth.deconnexion': 'Déconnexion',
    'auth.session_inactive': 'Session fermée pour inactivité',
    'auth.second_facteur_demande': 'Second facteur demandé',
    'auth.second_facteur_echec': 'Code du second facteur refusé',
    'auth.second_facteur_rejoue': 'Code du second facteur déjà servi — refusé',
    'auth.enrolement_requis': 'Enrôlement du second facteur exigé à la connexion',
    'auth.totp_active': 'Second facteur activé',
    'auth.totp_activation_demandee': 'Enrôlement du second facteur engagé',
    'auth.totp_desactive': 'Second facteur retiré',
    'auth.totp_reinitialise': 'Second facteur réinitialisé par un administrateur',
    'auth.poste_demande': 'Poste du jour à déclarer',
    'auth.poste_declare': 'Poste du jour déclaré',
    'auth.changement_mot_de_passe_requis': 'Mot de passe provisoire à remplacer',
    'auth.mot_de_passe_change': 'Mot de passe changé',
    'auth.echec_changement_mot_de_passe': 'Changement de mot de passe refusé',
    'auth.reinitialisation_demandee': 'Lien de réinitialisation demandé',
    'auth.reinitialisation_refusee': 'Lien de réinitialisation refusé',
    'auth.mot_de_passe_reinitialise': 'Mot de passe réinitialisé par lien',
    'auth.rejeu_refresh': 'Jeton de rafraîchissement rejoué — famille révoquée',
    'auth.refresh_inconnu': 'Jeton de rafraîchissement inconnu',
    // Sauvegardes (décision 96)
    'sauvegarde.telechargement_autorise': 'Téléchargement d’une sauvegarde autorisé par code',
    'sauvegarde.telechargement': 'Sauvegarde téléchargée',
    'sauvegarde.telechargement_interrompu': 'Téléchargement d’une sauvegarde interrompu',
    'sauvegarde.telechargement_refuse': 'Téléchargement d’une sauvegarde refusé',
    // Comptes
    'utilisateur.creation': 'Compte créé',
    'utilisateur.modification': 'Compte modifié',
    'utilisateur.deverrouillage': 'Compte déverrouillé',
    'utilisateur.mot_de_passe_provisoire': 'Mot de passe provisoire donné par un administrateur',
    // Rôles (ADR 0006)
    'role.creation': 'Rôle créé',
    'role.modification': 'Rôle modifié',
    'role.desactivation': 'Rôle désactivé',
    'role.reinitialisation': 'Rôle ramené à la matrice du code',
    // Sites et zones (ADR 0001, 0003)
    'site.creation': 'Site ouvert',
    'site.modification': 'Site modifié',
    'site.fermeture': 'Site fermé',
    'site.boxes': 'Box d’un site réglés',
    'site.chef_de_centre': 'Responsable d’antenne changé',
    'zone.creation': 'Zone créée',
    'zone.modification': 'Zone modifiée',
    // Configuration (CCT § 6)
    'configuration.modification': 'Paramètre modifié',
    'configuration.alerte_modifiee': 'Règle d’alerte modifiée',
    // Patients (M1)
    'personne.enrolement': 'Fiche patient créée',
    'personne.confirmation': 'Fiche patient confirmée ou infirmée',
    'personne.prise_en_charge': 'Confirmation d’une fiche prise en charge',
    'personne.prise_en_charge_relachee': 'Prise en charge d’une confirmation relâchée',
    'personne.prise_en_charge_reprise': 'Prise en charge d’une confirmation reprise par l’encadrement',
    'personne.note_chargee': 'Note de l’accueil pour la chargée posée, modifiée ou retirée',
    'personne.message_accueil': 'Message de la chargée à l’accueil posé, modifié ou retiré',
    'personne.modification': 'Fiche patient modifiée',
    'personne.depart_declare': 'Départ d’un salarié déclaré par la chargée de clientèle',
    'personne.depart_annule': 'Départ d’un salarié annulé — couverture rétablie',
    'personne.infirmation_annulee': 'Infirmation annulée — couverture rétablie',
    'personne.rattachement': 'Client externe rattaché à une entreprise',
    'personne.justificatif_pose': 'Pièce justificative d’un ayant droit jointe',
    'personne.justificatif_remplace': 'Pièce justificative d’un ayant droit remplacée',
    'personne.confirmation_sur_piece': 'Ayant droit confirmé sur pièce à l’accueil',
    'personne.ayant_droit_admis_sans_confirmation': 'Ayant droit admis sans confirmation, salarié au DNS',
    // Décision 360 — gardé pour les traces écrites avant la décision 404.
    'personne.enfant_admis_sans_confirmation': 'Enfant admis sans confirmation, parent au DNS',
    'personne.retrait_ayant_droit': 'Ayant droit confirmé retiré de la couverture',
    'personne.retrait_annule': 'Retrait d’un ayant droit annulé — couverture rétablie',
    'personne.reactivee_avec_son_salarie': 'Ayant droit réactivé avec son salarié sur vérification',
    'personne.verification_fermee_couverte': 'Vérification fermée d’elle-même : de nouveau couvert',
    'patient.photo_posee': 'Photo du patient ajoutée',
    'patient.photo_remplacee': 'Photo du patient remplacée',
    'patient.photo_retiree': 'Photo du patient retirée',
    'dossier_medical.lecture': 'Dossier médical consulté',
    // Décision 418 — le Super administrateur unique
    'super_admin.action': 'Geste du Super administrateur',
    'super_admin.creation': 'Super administrateur créé par le script d’exploitation',
    'super_admin.recuperation': 'Super administrateur récupéré par le script d’exploitation (mot de passe et second facteur)',
    'super_admin.centre': 'Super administrateur : centre de travail changé',
    // Import DNS (M1)
    'personne.fusion': 'Deux fiches fusionnées',
    'personne.fusion_annulee': 'Fusion de fiches défaite',
    'personne.doublon_ecarte': 'Doublon écarté : deux personnes différentes',
    'dns.depot': 'DNS déposé',
    'dns.telechargement': 'Fichier du DNS téléchargé',
    'dns.analyse': 'DNS analysé par le système',
    'dns.a_mapper': 'DNS envoyé à l’outil de mapping par le système',
    'dns.mapping': 'Colonnes du DNS désignées',
    'dns.colonne_matricule': 'Colonne du matricule gardée ou ignorée',
    'dns.lignes_personnes': 'Lignes écartées tranchées : personne ou non',
    'dns.reception': 'Réception d’un DNS papier ou PDF déclarée',
    'dns.scan': 'Scan du DNS rattaché',
    'dns.lecture': 'Scan du DNS lu par le système',
    'dns.saisie_commencee': 'Saisie du DNS commencée',
    'dns.saisie_terminee': 'Saisie du DNS terminée',
    'dns.saisie_reprise': 'Saisie du DNS reprise',
    'dns.validation': 'DNS validé',
    'dns.import_applique': 'DNS appliqué au référentiel',
    'dns.rejet': 'DNS rejeté',
    'dns.echec': 'Échec de traitement du DNS',
    'dns.a_repartir': 'DNS à plusieurs feuilles, en attente du choix',
    'dns.feuilles': 'Feuilles du DNS choisies',
    'dns.lecture_mapping': 'Colonnes du scan désignées',
    'dns.scan_retire': 'Page du scan retirée',
    'dns.annulation': 'Import DNS annulé',
    'dns.salaires_consultes': 'Salaires d’un DNS consultés, personne par personne',
    // Entreprises et offres (M1)
    'adherent.creation': 'Entreprise créée',
    'adherent.modification': 'Entreprise modifiée',
    'adherent.alias_retire': 'Autre nom d’une entreprise retiré',
    'adherent.contrat_ajoute': 'Contrat ajouté à une entreprise',
    'contrat.scan_pose': 'Scan du contrat signé joint',
    'contrat.scan_remplace': 'Scan du contrat signé remplacé',
    'contrat.scan_retire': 'Scan du contrat signé retiré',
    'contrat.population_modifiee': 'Salariés couverts d’un contrat renseignés',
    'adherent.suspension': 'Entreprise suspendue',
    'adherent.suspension_levee': 'Suspension levée',
    'adherent.resiliation': 'Résiliation enregistrée — préavis ouvert',
    'adherent.resiliation_annulee': 'Résiliation annulée',
    'adherent.resiliation_effective': 'Adhésion close au terme du préavis',
    'offre.creation': 'Offre créée',
    'offre.modification': 'Offre modifiée',
    // Registre d'affluences (M1)
    'visite.entree': 'Entrée d’un patient notée au registre',
    'visite.appel': 'Patient appelé en consultation',
    'visite.consultation_commencee': 'Consultation commencée : le patient appelé est entré',
    'visite.fin': 'Consultation terminée',
    'visite.prix_dentiste': 'Prix fixé par le dentiste pour un patient non couvert',
    'visite.prix_dentiste_correction': 'Prix du dentiste corrigé avant l’encaissement',
    'visite.prix_dentiste_retrait': 'Prix du dentiste retiré avant l’encaissement',
    'visite.suite_de_soin': 'Suite d’un soin dentiste déjà payé : visite d’origine choisie',
    'visite.suite_de_soin_retrait': 'Coche « suite d’un soin déjà payé » retirée',
    'visite.absence': 'Patient absent à l’appel',
    'visite.export_entreprise': 'Registre d’un mois exporté pour une entreprise',
    'visite.rappel': 'Patient rappelé sur l’écran d’appel',
    'visite.priorite': 'Priorité posée sur un patient en attente',
    'visite.annulation': 'Entrée annulée',
    'visite.ticket_reimprime': 'Ticket de passage réimprimé',
    'visite.entree_ressaisie': 'Entrée ressaisie après une coupure',
    'visite.patient_parti': 'Patient reparti sans entrer : sorti de « En attente de la chargée »',
    'visite.entree_deja_servi': 'Entrée confirmée : patient déjà servi le même jour pour cette prestation',
    'caisse.encaissement_deja_servi': 'Encaissement confirmé : patient déjà servi le même jour pour cette prestation',
    'caisse.attente_envoi_sans_chargee': 'Fiche à confirmer envoyée à la caisse sans attendre la chargée',
    'caisse.encaissement_sans_chargee': 'Payé sans attendre la chargée : fiche à confirmer encaissée',
    'caisse.tarif': 'Tarif de la caisse : ligne créée ou modifiée',
    'caisse.tarif_centre': 'Tarif de la caisse : prix propre à un centre posé, changé ou supprimé',
    'box.transfert': 'Patients transférés à un autre box',
    'box.creation': 'Box créé',
    'box.modification': 'Box modifié',
    'box.ouverture': 'Box ouvert par un praticien',
    'box.fermeture': 'Box fermé',
    'ecran_appel.creation': 'Écran d’appel déclaré',
    'ecran_appel.code': 'Code d’activation d’un écran d’appel renouvelé',
    'ecran_appel.activation': 'Écran d’appel activé sur une télé',
    'ecran_appel.activation_refusee': 'Code d’activation d’écran refusé',
    'ecran_appel.revocation': 'Écran d’appel révoqué',
    'ecran_appel.son_remplace': 'Son de l’annonce de l’écran d’appel remplacé',
    'ecran_appel.son_retabli': 'Son de l’annonce de l’écran d’appel rétabli à l’origine',
    // Mode dégradé (CP-12)
    'coupure.feuille_deposee': 'Feuille de coupure déposée',
    'coupure.lecture': 'Feuille de coupure lue par le système',
    'coupure.grille_enregistree': 'Grille de la feuille de coupure enregistrée',
    'coupure.ressaisie': 'Feuille de coupure ressaisie au registre',
    'coupure.feuille_abandonnee': 'Feuille de coupure abandonnée',
    'hors_ligne.export': 'Coffre hors ligne téléchargé par un poste',
    // Reprise des données (migration)
    'migration.referentiel': 'Référentiel des entreprises repris',
    'migration.titulaires': 'Salariés repris',
    'migration.ayants_droit': 'Ayants droit repris',
};
/**
 * Un filtre par famille d'événements. Le filtre serveur est un préfixe
 * d'action : une famille = un préfixe.
 */
export const FAMILLES_EVENEMENT_AUDIT = [
    { valeur: 'auth.', libelle: 'Connexions et sécurité' },
    { valeur: 'utilisateur.', libelle: 'Comptes' },
    { valeur: 'role.', libelle: 'Rôles' },
    { valeur: 'site.', libelle: 'Sites' },
    { valeur: 'zone.', libelle: 'Zones' },
    { valeur: 'configuration.', libelle: 'Configuration et alertes' },
    { valeur: 'personne.', libelle: 'Patients' },
    { valeur: 'dns.', libelle: 'Import DNS' },
    { valeur: 'adherent.', libelle: 'Entreprises' },
    { valeur: 'offre.', libelle: 'Offres' },
    { valeur: 'visite.', libelle: 'Registre et file d’attente' },
    { valeur: 'box.', libelle: 'Box' },
    { valeur: 'coupure.', libelle: 'Mode dégradé' },
    { valeur: 'hors_ligne.', libelle: 'Coffre hors ligne' },
    { valeur: 'patient.', libelle: 'Photos des patients' },
    { valeur: 'dossier_medical.', libelle: 'Accès au dossier médical' },
    { valeur: 'super_admin.', libelle: 'Super administrateur' },
];
/** Le libellé d'une action, ou son code quand on ne sait pas la nommer. */
export function libelleActionAudit(action) {
    return LIBELLE_ACTION_AUDIT[action] ?? action;
}
/** Minuscules, sans accents ni apostrophe typographique : « Écran » = « ecran ». */
function pourComparer(texte) {
    return texte
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/’/g, "'")
        .toLowerCase();
}
/** Les actions dont le libellé contient le terme cherché, accents et casse ignorés. */
export function actionsAuditDontLeLibelleContient(terme) {
    const cherche = pourComparer(terme.trim());
    if (cherche === '')
        return [];
    return Object.entries(LIBELLE_ACTION_AUDIT)
        .filter(([, libelle]) => pourComparer(libelle).includes(cherche))
        .map(([action]) => action);
}
/** Les actions connues, rangées dans l'ordre alphabétique de leur libellé. */
export const ACTIONS_AUDIT_PAR_LIBELLE = Object.keys(LIBELLE_ACTION_AUDIT).sort((a, b) => libelleActionAudit(a).localeCompare(libelleActionAudit(b), 'fr'));
//# sourceMappingURL=journal.js.map